import type { FetchConfig } from '@nlo/nuxt-common/api/helper-types';
import type { components, paths } from '~/generated/portal-api';
import { arrayOfAll, notEmpty } from '~/utils/typescript-helpers';
export type QueryParams = paths['/api/{pageId}/transactions']['get']['parameters']['query'];
export type QueryParamsSummary = paths['/api/transactions/summary/game']['get']['parameters']['query'];

export function createTransactionsApi(fetchConfig: FetchConfig) {
	const client = fetchConfig.createClient<paths>();

	return {
		getTransactions(pageId: string, queryParams?: QueryParams) {
			return client.GET('/api/{pageId}/transactions', { params: { path: { pageId }, query: queryParams } });
		},
		async getTransactionSummary(queryParams: QueryParamsSummary) {
			const [gameSummary, walletSummary] = await Promise.all([
				client.GET('/api/transactions/summary/game', { params: { query: queryParams } }),
				client.GET('/api/transactions/summary/wallet', { params: { query: queryParams } }),
			]);

			return {
				...gameSummary,
				...walletSummary,
			};
		},
	};
}

export const transactionFilterSettings = {
	transactionFilter: getFilters(),
	periodFilter: getTransactionPeriods(),
	transactionSummaryFilter: getFiltersForSummary(),
};

/**
 * Returns a list of period filters for the transaction and transaction summary call
 */
function getTransactionPeriods() {
	const arrayOfAllDateOptions = arrayOfAll<components['schemas']['DateOptions']>();
	const periods = arrayOfAllDateOptions([
		'PresentWeek',
		'PastWeek',
		'PastMonth',
		'PastYear',
		'PresentMonth',
		'PresentYear',
		'CustomDateRange',
	]);

	return getPeriodsWithLabels(periods);
}

/**
 * Returns a list of filters for the transaction call.
 */
function getFilters(): { key: components['schemas']['TransactionFilter']; label: string }[] {
	const arrayOfAllFilters = arrayOfAll<components['schemas']['TransactionFilter']>();
	const filterTypes = arrayOfAllFilters([
		'WALLET_ALL',
		'GAMES_ALL',
		'WALLET_TOP_UP',
		'WALLET_PAY_OUT',
		'EUROJACKPOT',
		'KRASLOTEN',
		'LOTTO',
		'LUCKY_DAY',
		'MILJOENENSPEL',
		'STAATSLOTERIJ',
		'TOTO',
		'TOTO_13',
	]);

	const excludedFilters: components['schemas']['TransactionFilter'][] = ['WALLET_PAY_OUT', 'WALLET_TOP_UP', 'GAMES_ALL', 'TOTO_13', 'TOTO'];

	return filterTypes
		.filter((filterType) => !excludedFilters.includes(filterType))
		.map((filterType) => ({ key: filterType, label: getLabelForFilter(filterType) }));
}

/**
 * Returns a list of filters for the transaction summary call.
 */
function getFiltersForSummary(): { key: components['schemas']['GameTransactionFilter']; label: string }[] {
	const arrayOfAllFilters = arrayOfAll<components['schemas']['GameTransactionFilter']>();
	const filterTypes = arrayOfAllFilters([
		'GAMES_ALL',
		'TOTO_13',
		'KRASLOTEN',
		'STAATSLOTERIJ',
		'MILJOENENSPEL',
		'LOTTO',
		'EUROJACKPOT',
		'LUCKY_DAY',
	]);

	const excludedFilters: components['schemas']['GameTransactionFilter'][] = ['GAMES_ALL', 'TOTO_13'];

	return filterTypes
		.filter((filterType) => !excludedFilters.includes(filterType))
		.map((filterType) => ({ key: filterType, label: getLabelForFilter(filterType) }));
}

function getPeriodsWithLabels(periods: components['schemas']['DateOptions'][]) {
	return periods
		.map((period) => {
			switch (period) {
				case 'PresentWeek':
					return {
						key: period,
						label: 'Huidige week',
					};
				case 'PastWeek':
					return {
						key: period,
						label: 'Vorige week',
					};
				case 'PastMonth':
					return {
						key: period,
						label: 'Vorige maand',
					};
				case 'PastYear':
					return {
						key: period,
						label: 'Vorig jaar',
					};
				case 'PresentMonth':
					return {
						key: period,
						label: 'Huidige maand',
					};
				case 'PresentYear':
					return {
						key: period,
						label: 'Huidig jaar',
					};
				case 'CustomDateRange':
					return {
						key: period,
						label: 'Eigen datum kiezen',
					};
				default: {
					return undefined;
				}
			}
		})
		.filter(notEmpty);
}

function getLabelForFilter(filterType: components['schemas']['TransactionFilter'] | components['schemas']['GameTransactionFilter']) {
	switch (filterType) {
		case 'WALLET_ALL':
			return 'Opwaarderen en uitbetalen';
		case 'TOTO':
			return 'Toto';
		case 'TOTO_13':
			return 'Toto 13';
		case 'KRASLOTEN':
			return 'Krasloten';
		case 'STAATSLOTERIJ':
			return 'Staatsloterij';
		case 'MILJOENENSPEL':
			return 'Miljoenenspel';
		case 'LOTTO':
			return 'Lotto';
		case 'EUROJACKPOT':
			return 'Eurojackpot';
		case 'LUCKY_DAY':
			return 'Lucky day';
		case 'GAMES_ALL':
			return 'Alle loterijen';
		case 'WALLET_PAY_OUT':
			return 'Wallet uitbetalingen';
		case 'WALLET_TOP_UP':
			return 'Wallet stortingen';
	}
}
